interface FinalCTASectionProps {
  onOpenForm: () => void;
}

export default function FinalCTASection({ onOpenForm }: FinalCTASectionProps) {
  return (
    <section className="relative bg-[#0d1117] py-24 md:py-36 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/d27c78890e6bb7b06f519240d11925bd.jpg"
          alt=""
          className="w-full h-full object-cover object-center opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0d1117]/70 via-[#0d1117]/50 to-[#0d1117]/90"></div>
      </div>

      {/* Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-emerald-500/8 rounded-full blur-3xl pointer-events-none"></div>

      <div className="relative z-10 max-w-4xl mx-auto px-4 md:px-8 text-center">

        {/* Label */}
        <div className="inline-flex items-center gap-2 bg-emerald-500/15 border border-emerald-500/25 rounded-full px-4 py-1.5 mb-10">
          <i className="ri-eye-line text-emerald-400" style={{ fontSize: '13px' }}></i>
          <span className="text-emerald-300 text-xs font-semibold tracking-wider uppercase">Free Preview — Zero Obligation</span>
        </div>

        {/* Strong close headline */}
        <h2 className="text-3xl md:text-5xl lg:text-[3.25rem] font-black text-white leading-[1.1] tracking-tight mb-6">
          If your website isn&apos;t{' '}
          <span className="bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent">
            generating business,
          </span>
          <br />
          it&apos;s costing you business.
        </h2>

        <p className="text-white/55 text-base md:text-lg mb-4 max-w-xl mx-auto leading-relaxed">
          Every day without a site that converts is a customer that went to your competitor instead.
        </p>

        {/* Tagline box */}
        <div className="max-w-2xl mx-auto bg-white/5 border border-emerald-500/20 rounded-2xl px-6 py-5 mb-10">
          <p className="text-white/80 text-base md:text-lg leading-relaxed font-medium">
            See what your website would look like with a{' '}
            <span className="text-emerald-400 font-bold">2026 modern design</span>{' '}
            — optimized for{' '}
            <span className="text-teal-300 font-semibold">AI &amp; Voice Search</span>{' '}
            — built to{' '}
            <span className="text-white font-semibold">convert visitors into leads.</span>
          </p>
        </div>

        {/* Primary CTA — oversized + glowing */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
          <button
            onClick={onOpenForm}
            className="whitespace-nowrap relative group inline-flex items-center justify-center gap-3 bg-gradient-to-r from-emerald-500 to-teal-400 text-white font-black text-lg md:text-xl px-12 py-6 rounded-full hover:scale-[1.03] transition-all duration-200 cursor-pointer"
            style={{ boxShadow: '0 0 40px rgba(16,185,129,0.45), 0 20px 60px rgba(16,185,129,0.2)' }}
          >
            <span className="absolute inset-0 rounded-full bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></span>
            <i className="ri-eye-line relative z-10" style={{ fontSize: '22px' }}></i>
            <span className="relative z-10">Request Your Free Website Preview</span>
            <i className="ri-arrow-right-line relative z-10" style={{ fontSize: '22px' }}></i>
          </button>
          <a
            href="tel:8004559670"
            className="whitespace-nowrap inline-flex items-center justify-center gap-2.5 bg-white/8 border border-white/20 text-white font-bold text-base md:text-lg px-8 py-5 rounded-full hover:bg-white/14 transition-all duration-200 cursor-pointer"
          >
            <i className="ri-phone-fill text-emerald-400" style={{ fontSize: '20px' }}></i>
            800-455-9670
          </a>
        </div>

        {/* Trust signals */}
        <div className="flex flex-wrap items-center justify-center gap-6">
          {[
            { icon: 'ri-timer-line', label: 'Preview ready in 48 hours' },
            { icon: 'ri-lock-line', label: 'No credit card required' },
            { icon: 'ri-award-line', label: '500+ businesses served' },
            { icon: 'ri-close-circle-line', label: 'Cancel anytime after 90 days' },
          ].map((t) => (
            <div key={t.label} className="flex items-center gap-2">
              <i className={`${t.icon} text-emerald-400`} style={{ fontSize: '14px' }}></i>
              <span className="text-white/40 text-xs">{t.label}</span>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}
